import { JSX, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Collapse from "@mui/material/Collapse";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { asTitle, setElement } from "utils/Utils";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";

export type ReportFilterProps = {
	labels: Array<string>;

	onUpdate: (labels: Array<string>) => void;
};

export default function ReportFilter(
	props: Readonly<ReportFilterProps>
): JSX.Element {
	const { t } = useTranslation();
	const [expanded, setExpanded] = useState(false);

	const [filter, setFilter] = useState<Array<string>>([]);

	useEffect(() => {
		props.onUpdate(filter);
	}, [filter]);

	const onChangeLabels = useCallback(
		(label: string, checked: boolean): void => {
			setFilter(previous => setElement(checked, label, previous));
		},
		[props.labels]
	);

	if (props.labels.length === 0) {
		return <></>;
	}

	return (
		<Box
			sx={{
				marginTop: 2,
				marginBottom: 2,
				paddingRight: 1,
				border: 1,
				borderColor: "divider",
				borderRadius: 1,
				backgroundColor: "background.paper"
			}}>
			<Grid container alignItems={"center"}>
				<Grid item>
					<IconButton onClick={() => setExpanded(!expanded)}>
						{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
					</IconButton>
				</Grid>
				<Grid item flexGrow={1}>
					<Typography variant="subtitle1">
						{asTitle(t("filter"))}
					</Typography>
				</Grid>
			</Grid>
			<Collapse
				in={expanded}
				timeout="auto"
				sx={{
					paddingLeft: 2,
					paddingRight: 2,
					paddingBottom: expanded ? 2 : 0
				}}>
				{props.labels.length > 0 && (
					<Grid
						item
						container
						xs={12}
						spacing={2}
						alignItems="center">
						<Grid item xs={4} sm={3} lg={2}>
							<Typography variant="subtitle2">
								{asTitle(t("labels"))}
							</Typography>
						</Grid>
						<Grid item xs={8} sm={9} lg={10}>
							{props.labels.map(label => (
								<FormControlLabel
									key={label}
									control={
										<Checkbox
											checked={filter.includes(label)}
											onChange={e =>
												onChangeLabels(
													label,
													e.target.checked
												)
											}
										/>
									}
									label={label}
								/>
							))}
						</Grid>
					</Grid>
				)}
			</Collapse>
		</Box>
	);
}
